import { FC } from 'react'
import { ListGroup, Badge, Spinner } from 'react-bootstrap'
import { NotificationType } from '../../types/notifications'

interface NotificationsSidebarProps {
  selectedType: NotificationType | 'ALL'
  setSelectedType: (type: NotificationType | 'ALL') => void
  typeCounts: Array<{ notificationType: string; unreadCount: number }>
  availableTypes: string[]
  loading: boolean
}

export const NotificationsSidebar: FC<NotificationsSidebarProps> = ({
  selectedType,
  setSelectedType,
  typeCounts,
  availableTypes,
  loading,
}) => {
  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      ALL: 'All Notifications',
      POLICY_NOTE_MENTION: 'Policy Mentions',
      POLICY_STATUS_CHANGE: 'Policy Status',
      POLICY_ASSIGNMENT: 'Policy Assignments',
      POLICY_ASSIGNMENT_REQUEST: 'Assignment Requests',
      POLICY_PAYMENT_VIEW: 'Payment Views',
      COMMISSION_CLAIM: 'Commission Claims',
      COMMISSION_APPROVED: 'Commission Approved',
      COMMISSION_REJECTED: 'Commission Rejected',
      POLICY_REMINDER: 'Reminders',
      LEAD_ASSIGNMENT: 'Lead Assignments',
      TICKET_ASSIGNED: 'Tickets Assigned',
      TICKET_STATUS_CHANGE: 'Ticket Status',
      TICKET_COMMENT: 'Ticket Comments',
      SIGNATURE_COMPLETED: 'Signature Completed',
      SIGNATURE_PENDING: 'Signature Pending',
      GENERAL: 'General',
    }
    return labels[type] || type
  }

  const getTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      ALL: 'bi-inbox-fill',
      POLICY_NOTE_MENTION: 'bi-chat-left-text-fill',
      POLICY_STATUS_CHANGE: 'bi-arrow-left-right',
      POLICY_ASSIGNMENT: 'bi-person-check-fill',
      POLICY_ASSIGNMENT_REQUEST: 'bi-person-fill-exclamation',
      POLICY_PAYMENT_VIEW: 'bi-credit-card-fill',
      COMMISSION_CLAIM: 'bi-cash-coin',
      COMMISSION_APPROVED: 'bi-check-circle-fill',
      COMMISSION_REJECTED: 'bi-x-circle-fill',
      POLICY_REMINDER: 'bi-alarm-fill',
      LEAD_ASSIGNMENT: 'bi-person-plus-fill',
      TICKET_ASSIGNED: 'bi-ticket-perforated',
      TICKET_STATUS_CHANGE: 'bi-arrow-repeat',
      TICKET_COMMENT: 'bi-chat-dots-fill',
      SIGNATURE_COMPLETED: 'bi-pen-fill',
      SIGNATURE_PENDING: 'bi-pen',
      GENERAL: 'bi-bell-fill',
    }
    return icons[type] || 'bi-bell-fill'
  }

  // Create a map for quick lookup of unread counts
  const countsMap = typeCounts.reduce((acc, item) => {
    acc[item.notificationType] = item.unreadCount
    return acc
  }, {} as Record<string, number>)

  // Calculate total unread
  const totalUnread = typeCounts.reduce((sum, item) => sum + item.unreadCount, 0)

  // Only show "ALL" and types that have data
  const allTypes = ['ALL', ...availableTypes]

  return (
    <div className='card card-flush'>
      <div className='card-header'>
        <h3 className='card-title'>Categories</h3>
      </div>
      <div className='card-body p-0'>
        {loading ? (
          <div className='text-center py-10'>
            <Spinner animation='border' size='sm' />
          </div>
        ) : (
          <ListGroup variant='flush'>
            {allTypes.map((type) => {
              const unreadCount = type === 'ALL' ? totalUnread : countsMap[type] || 0
              const isActive = selectedType === type

              return (
                <ListGroup.Item
                  key={type}
                  action
                  active={isActive}
                  onClick={() => setSelectedType(type as NotificationType | 'ALL')}
                  className='d-flex justify-content-between align-items-center'
                  style={{ cursor: 'pointer' }}
                >
                  <div className='d-flex align-items-center gap-2'>
                    <i className={`${getTypeIcon(type)} fs-5`}></i>
                    <span>{getTypeLabel(type)}</span>
                  </div>
                  {unreadCount > 0 && (
                    <Badge
                      bg={isActive ? 'light' : 'primary'}
                      text={isActive ? 'dark' : 'white'}
                      pill
                    >
                      {unreadCount}
                    </Badge>
                  )}
                </ListGroup.Item>
              )
            })}
          </ListGroup>
        )}
      </div>
    </div>
  )
}
